import React from 'react';
import { Copy, ExternalLink, Twitter, Globe, Send, Check, Menu, X } from 'lucide-react';
import { useState, useEffect } from 'react';

const Footer = () => {
  const [copiedAddress, setCopiedAddress] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isContractMenuOpen, setIsContractMenuOpen] = useState(false);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setIsContractMenuOpen(false);
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const contracts = [
    {
      chain: "Arbitrum",
      logo: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 2L20 8V16L12 22L4 16V8L12 2Z" />
          <path d="M12 22V16" />
          <path d="M20 8L12 16" />
          <path d="M4 8L12 16" />
        </svg>
      ),
      address: "0x13A7DeDb7169a17bE92B0E3C7C2315B46f4772B3",
      explorer: "https://arbiscan.io/token/0x13A7DeDb7169a17bE92B0E3C7C2315B46f4772B3"
    },
    {
      chain: "Solana",
      logo: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M4 4h16" />
          <path d="M4 12h16" />
          <path d="M4 20h16" />
        </svg>
      ),
      address: "2HrZ5R18H48b8ptL3n8N5zX65zDB4o2cMCHcP3QfSfye",
      explorer: "https://solscan.io/token/2HrZ5R18H48b8ptL3n8N5zX65zDB4o2cMCHcP3QfSfye"
    }
  ];

  const socialLinks = [
    {
      name: "Twitter",
      url: "https://x.com/boopthecoin",
      icon: <Twitter className="w-4 h-4" />
    },
    {
      name: "TikTok",
      url: "https://www.tiktok.com/@boopthecoinofficial",
      icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
        <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
      </svg>
    },
    {
      name: "Instagram",
      url: "https://www.instagram.com/boopthecoinofficial/",
      icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
        <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
      </svg>
    },
    {
      name: "Telegram",
      url: "https://t.me/boopthecoin",
      icon: <Send className="w-4 h-4" />
    },
    {
      name: "CoinGecko",
      url: "https://www.coingecko.com/tr/coins/boop-2",
      icon: <Globe className="w-4 h-4" />
    }
  ];

  const handleCopy = (text, chain) => {
    navigator.clipboard.writeText(text);
    setCopiedAddress(chain);
    setTimeout(() => setCopiedAddress(null), 2000);
  };

  const ContractItem = ({ contract, isMobileMenu = false }) => (
    <div className={`flex items-center transition-all duration-200 whitespace-nowrap
      ${isMobileMenu ? 'bg-gray-800 rounded-lg p-3 w-full hover:bg-gray-750' : 'gap-2'}`}>
      <div className={`flex items-center ${isMobileMenu ? 'justify-between w-full' : 'gap-2'}`}>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 min-w-[100px]">
            <span className="text-gray-300">
              {contract.logo}
            </span>
            <span className="text-gray-300 font-medium">
              {contract.chain}
            </span>
          </div>
          {isMobileMenu && (
            <code className="text-sky-400 text-sm font-mono">
              {contract.address.slice(0, 6)}...{contract.address.slice(-4)}
            </code>
          )}
        </div>
        <div className="flex items-center gap-2">
          <a
            href={contract.explorer}
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-500 hover:text-gray-300 transition-colors p-1"
          >
            <ExternalLink className="w-4 h-4" />
          </a>
          <button
            onClick={() => handleCopy(contract.address, contract.chain)}
            className="text-gray-500 hover:text-gray-300 transition-colors p-1"
          >
            {copiedAddress === contract.chain ? (
              <Check className="w-4 h-4 text-green-500" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className="h-[52px]" />
      
      <footer className="fixed bottom-0 left-0 right-0 w-full bg-white border-t border-gray-800 z-50">
        <div className="bg-gray-900 text-white py-2">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-row justify-between items-center">
              {/* Contracts menu button */}
              <div className="relative">
              <button
                  onClick={() => setIsContractMenuOpen(!isContractMenuOpen)}
                  className="flex items-center gap-2 p-2 text-gray-400 hover:text-white transition-all duration-200"
                >
                  <Menu className="w-4 h-4" />
                  <span className="text-sm">Contracts</span>
                </button>

                {/* Contract Menu Popup */}
                {isContractMenuOpen && (
                  <>
                    <div 
                      className="fixed inset-0 bg-gray-900 bg-opacity-50 z-50"
                      onClick={() => setIsContractMenuOpen(false)}
                    />
                    <div className="absolute bottom-12 left-0 bg-gray-900 rounded-lg shadow-lg z-50 min-w-[320px] p-2">
                      <div className="flex flex-col gap-2">
                        <div className="flex justify-between items-center px-2 py-1 border-b border-gray-800">
                          <span className="text-sm font-medium text-gray-300">Contract Addresses</span>
                          <button 
                            onClick={() => setIsContractMenuOpen(false)}
                            className="text-gray-400 hover:text-white p-1"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        {contracts.map((contract, index) => (
                          <ContractItem key={index} contract={contract} isMobileMenu={true} />
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
              
              {/* Social media links */}
              <div className="flex items-center gap-2 md:gap-3">
                {socialLinks.map((link, index) => (
                  <a
                    key={index}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 p-2 text-gray-400 hover:text-white transition-all duration-200"
                    title={link.name}
                  >
                    {link.icon}
                    <span className="text-sm hidden md:inline">{link.name}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;